/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Clock } from 'lucide-react';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { DonutChart, type DonutSlice } from '../../components/ui/donut-chart.js';
import { ProgressBar } from '../../components/ui/progress-bar.js';
import { RevenueSessionsChart } from '../../components/ui/revenue-sessions-chart.js';
import {
  type DistributionSlice,
  type MonthlyPoint,
  type TherapistPerf,
  type UpcomingSession,
} from '../../lib/api/dashboard.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { initialsOf } from '../../lib/utils/initials.js';
import { formatInr } from '../../lib/utils/money.js';

import { DashboardCard } from './dashboard-card.js';

const SLICE_COLORS = ['#5b47db', '#c084fc', '#10b981', '#f59e0b', '#0ea5e9', '#94a3b8'];

/** "Revenue & Sessions" — revenue area + sessions line over the trailing 6 months. */
export function RevenueSessionsCard({
  monthly,
}: {
  readonly monthly: readonly MonthlyPoint[];
}): JSX.Element {
  const { t } = useTranslation();
  const data = monthly.map((m) => ({
    label: new Date(m.monthIso).toLocaleString('en', { month: 'short' }),
    revenue: Math.round(m.revenuePaise / 100),
    sessions: m.sessions,
  }));
  return (
    <DashboardCard
      title={t('admin.dashboard.revenueSessions')}
      subtitle={t('admin.dashboard.last6Months')}
    >
      <RevenueSessionsChart
        data={data}
        revenueName={t('admin.dashboard.revenue')}
        sessionsName={t('admin.dashboard.sessions')}
      />
    </DashboardCard>
  );
}

/** "Client Distribution" — donut by therapy type with a counted legend. */
export function DistributionCard({
  distribution,
}: {
  readonly distribution: readonly DistributionSlice[];
}): JSX.Element {
  const { t } = useTranslation();
  const slices: DonutSlice[] = distribution.map((d, i) => ({
    label: d.label,
    value: d.count,
    color: SLICE_COLORS[i % SLICE_COLORS.length] ?? '#94a3b8',
  }));
  return (
    <DashboardCard
      title={t('admin.dashboard.distribution')}
      subtitle={t('admin.dashboard.distributionSub')}
    >
      {slices.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">{t('admin.dashboard.noData')}</p>
      ) : (
        <div>
          <DonutChart slices={slices} />
          <ul className="mt-4 space-y-2">
            {slices.map((s) => (
              <li key={s.label} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: s.color }}
                    aria-hidden="true"
                  />
                  {s.label}
                </span>
                <span className="font-medium text-slate-700 dark:text-slate-200">{s.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DashboardCard>
  );
}

function PerformanceRow({
  row,
  maxSessions,
}: {
  readonly row: TherapistPerf;
  readonly maxSessions: number;
}): JSX.Element {
  const { t } = useTranslation();
  const pct = maxSessions > 0 ? Math.round((row.sessions / maxSessions) * 100) : 0;
  return (
    <li className="flex items-center gap-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-xs font-bold text-white">
        {initialsOf(row.name)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
          {row.name}
        </p>
        <p className="text-xs text-slate-400">
          {t('admin.dashboard.sessionsUnit', { count: row.sessions })}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
          {formatInr(row.revenuePaise)}
        </p>
        {row.satisfactionPct !== null && (
          <p className="text-xs text-slate-400">
            {t('admin.dashboard.satisfaction', { pct: row.satisfactionPct })}
          </p>
        )}
      </div>
      <div className="hidden w-20 shrink-0 sm:block">
        <ProgressBar value={pct} />
      </div>
    </li>
  );
}

/** "Therapist Performance" — top performers by session volume + satisfaction. */
export function PerformanceCard({
  rows,
}: {
  readonly rows: readonly TherapistPerf[];
}): JSX.Element {
  const { t } = useTranslation();
  const maxSessions = Math.max(...rows.map((r) => r.sessions), 1);
  return (
    <DashboardCard
      title={t('admin.dashboard.performance')}
      subtitle={t('admin.dashboard.performanceSub')}
      action={
        <Link
          to="/admin/therapists"
          className="focus-ring text-sm font-medium text-[var(--accent-strong)]"
        >
          {t('admin.dashboard.viewAll')}
        </Link>
      }
    >
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">{t('admin.dashboard.noData')}</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <PerformanceRow key={row.id} row={row} maxSessions={maxSessions} />
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}

/** "Upcoming Sessions" — today's IST schedule across the clinic. */
export function UpcomingCard({
  sessions,
}: {
  readonly sessions: readonly UpcomingSession[];
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <DashboardCard
      title={t('admin.dashboard.upcoming')}
      subtitle={t('admin.dashboard.upcomingSub')}
    >
      {sessions.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">{t('admin.dashboard.noUpcoming')}</p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {sessions.map((s) => (
            <li key={s.id} className="flex items-start gap-3 py-3 first:pt-0">
              <span className="bg-[var(--accent)]/10 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--accent-strong)]">
                <Clock className="h-4 w-4" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                  {s.clientName}
                </p>
                {s.therapistName !== null && (
                  <p className="truncate text-xs text-slate-400">{s.therapistName}</p>
                )}
                {s.sessionType !== null && (
                  <p className="truncate text-xs text-slate-400">{s.sessionType}</p>
                )}
              </div>
              <span className="shrink-0 text-sm font-medium text-[var(--accent-strong)]">
                {formatIst(s.startIso, 'h:mm a')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}
